import { getMentionsFromCommentBody } from "@liveblocks/client";
import { useSelf, useThreads } from "@liveblocks/react/suspense";
import { Thread } from "@liveblocks/react-ui";
import { useAppDispatch, useAppSelector } from "@store";
import { setActiveThreadId } from "@store/story";
import { getActiveThreadId, getCurrentPayloadId } from "@store/story/selectors";
import { scrollToEntity } from "@store/story/utils";
import { useCallback, useEffect, useRef } from "react";
import { useDocPermissions } from "@/components/access/use-doc-permissions";
import { cn } from "@/lib/cn";
import { trackContent } from "@/pages/editor/utils";
import { Suspense } from "../../components/Suspense";
import { CollaborationEnabledProvider, useFilter } from "../../providers";
import "../../styles/_comments.scss";
import { Empty } from "./Empty";
import { Error } from "./Error";
import { Skeleton } from "./Skeleton";

export const List = () => {
  return (
    <CollaborationEnabledProvider fallback={<Empty />}>
      <Suspense fallback={<Error />} feature="Threads" loader={<Skeleton />}>
        <Content />
      </Suspense>
    </CollaborationEnabledProvider>
  );
};
const Content = () => {
  const dispatch = useAppDispatch();
  const { filter, isForMeFilter } = useFilter();
  const storyId = useAppSelector(getCurrentPayloadId);
  const activeThreadId = useAppSelector(getActiveThreadId);
  const { threads, hasFetchedAll, fetchMore, isLoading } = useThreads({
    query:
      filter === "all"
        ? { metadata: { storyId } }
        : {
            resolved: filter === "resolved",
            metadata: { storyId },
          },
  });

  const self = useSelf((me) => me.id);
  // Send flow: a viewer reads the comments but can't reply, resolve or react.
  const { canComment } = useDocPermissions();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    if (!containerRef.current || isLoading || hasFetchedAll) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const scrollThreshold = 100; // pixels from bottom to trigger load

    if (scrollHeight - (scrollTop + clientHeight) < scrollThreshold) {
      fetchMore();
    }
  }, [isLoading, hasFetchedAll, fetchMore]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (!threads?.length) return;
    const url = new URL(window.location.href);
    if (url.searchParams.has("thread_id")) {
      const threadId = url.searchParams.get("thread_id");
      if (threadId) {
        const thread = threads.find((t) => t.id === threadId);
        if (thread) {
          dispatch(setActiveThreadId(thread.metadata.entityId as string));
          scrollToEntity(thread.metadata.entityId as string);
        }
      }
      url.searchParams.delete("thread_id");
    }
    window.history.replaceState({}, "", url.toString());
  }, [dispatch, threads]);

  useEffect(() => {
    trackContent("click open comments panel");
    return () => {
      dispatch(setActiveThreadId(null));
    };
  }, [dispatch]);

  let filtered = threads;
  if (isForMeFilter && self) {
    filtered = threads?.filter((thread) => {
      return thread.comments.some((comment) => {
        if (!comment.body) {
          return false;
        }
        const mentions = getMentionsFromCommentBody(comment.body);
        const mentionedIds = mentions
          .filter((mention) => mention.kind === "user")
          .map((mention) => mention.id);
        return mentionedIds.includes(self);
      });
    });
  }

  if (!filtered?.length) return <Empty />;

  return (
    <div
      ref={containerRef}
      className="scrollbar tw-h-[calc(100%_-_16px)] tw-space-y-4 tw-overflow-y-auto tw-p-4 tw-pt-2"
      id="vyqdiRYC2qsIgAX"
    >
      <span className="tw-block tw-h-[100px]"></span>
      {filtered?.map((thread) => {
        const id = thread.metadata.entityId as string;
        return (
          <div
            key={thread.id}
            className={cn(
              "tw-relative tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-blue-50 tw-bg-white tw-shadow-sm",
              {
                "after:tw-pointer-events-none after:tw-absolute after:tw-inset-0 after:tw-rounded-[7px] after:tw-border-2 after:tw-border-solid after:tw-border-primary-base after:tw-content-['']":
                  id === activeThreadId,
              },
            )}
            onClick={() => {
              dispatch(setActiveThreadId(id));
              scrollToEntity(id);
              trackContent("click scroll to comment");
            }}
          >
            <Thread
              thread={thread}
              showComposer={canComment ? undefined : false}
              showResolveAction={canComment}
              showActions={canComment ? undefined : false}
              onResolvedChange={(resolved) => {
                if (!resolved) return;
                trackContent("click resolve comment");
              }}
            />
          </div>
        );
      })}
    </div>
  );
};
